import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import Paper from 'components/paper';
import { InfoIcon } from 'lucide-react';
import React, { useEffect, useRef } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { usePost } from 'hooks/use-post';
import ProductionCostSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/finance/production-cost-section';
import SalesPriceSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/finance/sales-price-section';
import StepFooter from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-footer';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { useQueryClient } from '@tanstack/react-query';
import TextField from 'components/text-field';
import titleCase from 'utils/title-case';
import { putFinanceData } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-put';
import TreatmentCostSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/finance/treatment-cost-section';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ScenarioSetupStepProps } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-setup-step-props.type';
import { UpdateScenarioFinancialDataInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-financial-data-input.type';

const StepFinance: React.FC<ScenarioSetupStepProps> = ({ onClick, activeStep, canProceed }) => {
  const params = useParams<R2ScenarioParams>();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const { scenarioId, globalSiteId } = useParams<R2ScenarioParams>();
  const hasReset = useRef(false);
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const form = useForm<UpdateScenarioFinancialDataInput>({
    defaultValues: {},
    mode: 'onChange',
    shouldUnregister: true,
  });

  const {
    control,
    formState: { isDirty },
    reset,
  } = form;

  useEffect(() => {
    if (hasReset.current || !scenario?.financialData) return;

    const { currencyPerUnit, scenarioId, treatmentsPerCage, costOfSingleTreatment, ...numericFields } = scenario.financialData;

    // Defaults zero values to undefined so that the form inputs are empty instead of showing 0, which is more intuitive for the user.
    reset({
      treatmentsPerCage,
      costOfSingleTreatment,
      ...Object.fromEntries(Object.entries(numericFields).map(([k, v]) => [k, v === 0 ? undefined : v])),
    });
    hasReset.current = true;
  }, [scenario?.financialData]);

  const updateFinance = usePost<void, UpdateScenarioFinancialDataInput>(
    (request) => putFinanceData(String(scenarioId), String(globalSiteId), request),
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      queryClient.invalidateQueries({
        queryKey: ['getR2Scenario', params.globalSiteId, params.scenarioId],
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.error.default' }), error: error },
      });
    }
  );

  const handleSubmit = (data: UpdateScenarioFinancialDataInput, isSave: boolean) => {
    if (!isDirty && !!scenario?.financialData && !isSave) {
      onClick(activeStep + 1);
      return;
    }
    updateFinance.mutate(data, isSave ? undefined : { onSuccess: () => onClick(activeStep + 1) });
  };

  const handleNext = form.handleSubmit((data) => handleSubmit(data, false));

  // Resets form back to the last saved values from the server.
  const handleReset = () => {
    const defaultValues = form.control._defaultValues;
    reset(defaultValues);
  };

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <FormProvider {...form}>
        <Typography>Finance</Typography>
        <Divider />
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <Typography>Treatments</Typography>
          <TreatmentCostSection />
        </Box>
        <Divider />
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <InfoIcon size={16} />
            <Typography color={'text.secondary'} fontSize={14}>
              All inputs below are global defaults.
            </Typography>
          </Box>
          <Button variant="contained" disabled={!isDirty} onClick={handleReset}>
            Reset to defaults
          </Button>
        </Box>
        <TextField
          fullWidth
          infoText="Currency can be changed in the previous step."
          disabled
          value={titleCase(scenario?.financialData?.currency?.toUpperCase() ?? 'N/A')}
          label="Currency"
        />
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <Typography>Production costs</Typography>
          <ProductionCostSection />
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <Typography>Sales price per size</Typography>
          <SalesPriceSection />
        </Box>
        <Divider />
        <StepFooter
          onPrev={() => onClick(activeStep - 1)}
          onNext={handleNext}
          onSkip={() => onClick(activeStep + 1)}
          disablePrev={updateFinance.isPending}
          disableSave
          disableNext={!canProceed || updateFinance.isPending}
          nextIcon={updateFinance.isPending ? <CircularProgress size={20} /> : undefined}
        />
      </FormProvider>
    </Paper>
  );
};

export default StepFinance;
